import { addFieldSufixes, combineComparisonRows, createColumnsForComparisonGrid } from "./insComparisonGridHelper";
import { DatapointIdStandardEnum } from "api/ux/src";
import { comparisonAliases } from "common/constants";
import { InspectDataComparisonTab, InspectDataTab } from "components/modal/widget-inspect-data/types";
import { CustomColumnType } from "components/widgets/grid/grid-column-types";
import { isAccessPreqinProEnabled, isPermissionsEnabled, isPreqinClientEnabled } from "hooks/use-tenant-settings";
const extractRows = (data, columnsWhichAreArrays) => {
    let rows = [];
    for (const item of data) {
        if (item.data && item.data.length > 0) {
            rows = rows.concat(extractRows(item.data, columnsWhichAreArrays));
        }
        else {
            const elementToAdd = { ...item.measures, ...item.customMeasures };
            if (columnsWhichAreArrays.length > 0) {
                elementToAdd.date = item.date;
            }
            rows.push(elementToAdd);
        }
    }
    return rows;
};
const getGroupsValues = (measure, data) => {
    const children = {};
    if (data) {
        for (const group of data) {
            if (typeof group.dimension === "string" && group.measures) {
                children[group.dimension] = {
                    children: getGroupsValues(measure, group.data),
                    value: group.measures[measure]
                };
            }
        }
    }
    return children;
};
const extractTotals = (measureItems) => {
    const result = {};
    if (measureItems.length === 1) {
        const [overall] = measureItems;
        for (const [key, value] of Object.entries(overall.measures || {})) {
            result[key] = {
                children: getGroupsValues(key, overall.data),
                value
            };
        }
    }
    return result;
};
const getColumnsWhichAreArrays = (columns) => {
    const valuesToSetAsArray = [];
    columns.forEach((col) => {
        if (col?.type === CustomColumnType.InsSparklineColumn) {
            valuesToSetAsArray.push(col);
        }
        const columnWithArrayDefinition = col.children?.find((child) => child.type === CustomColumnType.InsSparklineColumn);
        if (columnWithArrayDefinition) {
            valuesToSetAsArray.push(columnWithArrayDefinition);
        }
    });
    return valuesToSetAsArray;
};
const getIndexForNewItem = (item, fieldToChange, newItem) => {
    let indexToInsert = item[fieldToChange].findIndex((i) => new Date(newItem.x.date) < new Date(i.x.date));
    if (indexToInsert < 0) {
        if (item[fieldToChange].length) {
            indexToInsert = item[fieldToChange].length;
        }
        else {
            indexToInsert = 0;
        }
    }
    return indexToInsert;
};
/*
 * The global behavior is to have first the "real" rows and then the history extra rows that we attach/merge to the latters
 * Sometimes order is not correct, or we have "duplicates" on same date because for instance Fund Geography changed between inception and as of date
 * So solution to distinguish them is by counting measures because "real" rows will have both history/array and non-history measures
 */
const keepNonHistoryRowsToWhichArrayMeasuresShouldBeAttachedAtTheTop = (rows) => {
    const numberOfMeasures = (r) => Object.entries(r || {}).length;
    return [...rows].sort((a, b) => numberOfMeasures(b) - numberOfMeasures(a));
};
const getTransformedRows = (rows, columns, columnsWhichAreArrays) => {
    const transformedRows = [];
    if (columnsWhichAreArrays.length === 0) {
        return rows;
    }
    keepNonHistoryRowsToWhichArrayMeasuresShouldBeAttachedAtTheTop(rows).forEach((row) => {
        let item = transformedRows[row.id];
        if (!item) {
            item = { ...row };
            transformedRows[row.id] = item;
        }
        columnsWhichAreArrays.forEach((rsa) => {
            const fieldToChange = rsa.field;
            if (!Array.isArray(item[fieldToChange])) {
                item[fieldToChange] = [];
            }
            let newItem = null;
            if (row[fieldToChange]?.tooltipValue) {
                newItem = {
                    x: {
                        date: row.date,
                        label: rsa.label,
                        toString: () => row.date?.toString(),
                        tooltipValue: row[fieldToChange].tooltipValue
                    },
                    y: row[fieldToChange]?.value
                };
            }
            else {
                newItem =
                    {
                        x: {
                            date: row.date,
                            label: rsa.label,
                            toString: () => row.date?.toString()
                        },
                        y: row[fieldToChange]
                    };
            }
            if (newItem.y !== undefined) {
                const indexToInsert = getIndexForNewItem(item, fieldToChange, newItem);
                item[fieldToChange].splice(indexToInsert || 0, 0, newItem);
            }
        });
    });
    return Object.values(transformedRows);
};
const getRowsAndTotals = (response, columns) => {
    const columnsWhichAreArrays = getColumnsWhichAreArrays(columns);
    const extractedRows = extractRows(response, columnsWhichAreArrays);
    const transformedRows = getTransformedRows(extractedRows, columns, columnsWhichAreArrays);
    const totals = extractTotals(response);
    return { rows: transformedRows, totals };
};
export const getRowsAndTotalsForComparison = (response, columns) => {
    const differenceEntityAlias = comparisonAliases.diff(comparisonAliases.main, comparisonAliases.comparate(0));
    const columnsWhichAreArrays = getColumnsWhichAreArrays(columns);
    const extractedMainRows = extractRows(response.find((x) => x.entityAlias === comparisonAliases.main)?.items, columnsWhichAreArrays);
    const extractedComparateRows = extractRows(response.find((x) => x.entityAlias === comparisonAliases.comparate(0))?.items, columnsWhichAreArrays);
    const extractedDifferenceRows = extractRows(response.find((x) => x.entityAlias === differenceEntityAlias)?.items, columnsWhichAreArrays);
    const [dimension] = response[0].dimensions;
    const columnsToTriple = Object.keys(Object.assign({}, ...extractedDifferenceRows));
    const mainPortfolioName = extractedMainRows[0].PortfolioName;
    const comparisonPortfolioName = extractedComparateRows[0].PortfolioName;
    createColumnsForComparisonGrid(columns, columnsToTriple, mainPortfolioName, comparisonPortfolioName, dimension);
    addFieldSufixes(extractedMainRows, extractedComparateRows, extractedDifferenceRows, columnsToTriple, differenceEntityAlias, dimension);
    const rows = combineComparisonRows(extractedMainRows, extractedComparateRows, extractedDifferenceRows, dimension);
    return { rows, totals: {} };
};
export const insDatapointGridWidgetAdapterData = (response, columns, inspectDataTab) => {
    if (inspectDataTab === InspectDataTab.Portfolio) {
        return getRowsAndTotalsForComparison(response, columns);
    }
    if (inspectDataTab === InspectDataComparisonTab.MainPortfolio) {
        return getRowsAndTotals(response.find((x) => x.entityAlias === comparisonAliases.main)?.items, columns);
    }
    if (inspectDataTab === InspectDataComparisonTab.ComparatePortfolio) {
        return getRowsAndTotals(response.find((x) => x.entityAlias === comparisonAliases.comparate(0))?.items, columns);
    }
    return getRowsAndTotals(response, columns);
};
const defaultMaps = new Map([
    [DatapointIdStandardEnum.FundFundName, { path: "/fund/", value: DatapointIdStandardEnum.FundFundId }],
    [DatapointIdStandardEnum.AssetAssetName, { path: "/asset/", value: DatapointIdStandardEnum.AssetAssetId }],
    [DatapointIdStandardEnum.ManagerManagerName, { path: "/manager/", value: DatapointIdStandardEnum.ManagerManagerId }],
    [DatapointIdStandardEnum.PortfolioPortfolioName, { path: "/portfolio/", value: DatapointIdStandardEnum.PortfolioPortfolioId }],
    ["PreqinFund.Name", { path: "https://pro.preqin.com/funds/", value: "PreqinFund.Id" }],
    ["Fund.PreqinBenchmarkName", { path: "https://pro.preqin.com/analysis/benchmarks/privateCapital/market/", value: "Fund.PreqinBenchmarkId" }]
]);
const optionsMapParams = new Map([
    [DatapointIdStandardEnum.FundFundId, [{ name: "investorId", value: DatapointIdStandardEnum.FundInvestorId }]],
    [DatapointIdStandardEnum.AssetAssetId, [{ name: "investorId", value: DatapointIdStandardEnum.AssetInvestorId }]]
]);
const extractLinkColumns = (columns) => {
    const links = [];
    const isPermitted = isAccessPreqinProEnabled() && isPreqinClientEnabled() && isPermissionsEnabled();
    for (const column of columns) {
        const def = column, groupDef = column;
        if (def && def.type && (def.type.includes(CustomColumnType.InsDatapointLinkColumn) ||
            (def.type.includes(CustomColumnType.InsPreqinProColumn) && isPermitted))) {
            links.push(def);
        }
        if (groupDef && groupDef.children?.length) {
            links.push(...extractLinkColumns(groupDef.children));
        }
    }
    return links;
};
const extractDatapoints = (datapointsParams) => {
    const measures = datapointsParams.flatMap((p) => p.measures || []) || [];
    const dimensions = datapointsParams.flatMap((p) => p.dimensions || []) || [];
    return measures.concat(dimensions);
};
/*
 * There is a long-standing type error in widgets componentProps type measures and dimensions are not typed properly
 * This function is to get around that
 */
const getDatapointIdTweak = (datapoint) => datapoint?.standardDatapoint ?? datapoint?.datapoint?.standard?.toString();
const getParameters = (datapoints, id) => {
    const parameters = [];
    if (id && optionsMapParams.has(id)) {
        for (const param of optionsMapParams.get(id) || []) {
            const datapoint = datapoints.find((d) => getDatapointIdTweak(d) === param.value);
            if (datapoint) {
                parameters.push({ field: datapoint.name ?? "", name: param.name });
            }
        }
    }
    return parameters;
};
export const getDefaultMappings = (datapointsParams, columns) => {
    const mappings = [];
    if (datapointsParams?.length && columns?.length) {
        const cols = extractLinkColumns(columns);
        const datapoints = extractDatapoints(datapointsParams);
        for (const col of cols) {
            const { field } = col;
            const datapoint = datapoints.find((d) => d.name === col.field);
            const standard = getDatapointIdTweak(datapoint);
            if (field && standard && defaultMaps.has(standard)) {
                const entry = defaultMaps.get(standard);
                const id = datapoints.find((d) => getDatapointIdTweak(d) === entry?.value);
                if (id?.name) {
                    mappings.push({ field, id: id.name, params: getParameters(datapoints, entry?.value), path: entry?.path ?? "" });
                }
            }
        }
    }
    return mappings;
};
